/**
 * The simulation world: entity store, fixed-step tick, and the shift script engine.
 *
 * This is the sacred layer (plan B2). It imports nothing from Babylon, the DOM or the UI,
 * runs headless under Vitest, and is advanced only by `tick(dt)` in exact 1/60 s steps.
 * All randomness comes from one seeded Rng, so a seed plus an input log replays a session
 * byte for byte.
 *
 * The renderer reads `snapshot()` and drains `events`; it never reaches in and mutates.
 */

import type {
  Vessel, Projectile, Resources, RigState, ShiftProgress, SimSnapshot, EntityId, Cargo, WeaponId,
} from './types';
import { EventQueue } from './events';
import { Rng, hashSeed } from './rng';
import { buildWaveField, waterHeightAt, type WaveField } from './waves';
import {
  WEATHER_PRESETS, cloneWeather, lerpWeather, aimDisturbance,
  type WeatherState, type WeatherPresetName,
} from './weather';
import {
  spawnAtBearing, createVessel, stepVesselMotion, stepFriendlyApproach, applySeparation,
  rangeToRig, findVessel, resetEntityIds,
} from './ships';
import { stepHostileAi, type AiContext } from './ai';
import { updateDetection } from './detection';
import {
  createKrakenState, stepKraken, registerMantleHit, registerChargeNear, registerEyeHit,
  krakenDamageFactor, assaultRingPlacement, tentacleRingPlacement, type KrakenState,
} from './kraken';
import { KRAKEN } from '../content/boss';
import {
  ProjectilePool, stepProjectiles, detonationDamage, solveElevation, timeOfFlight,
} from './ballistics';
import {
  tickProduction, applyCargo, applyLoss, applyRigDamage, isDefeated, reloadMagazine,
} from './economy';
import { vesselSpec, ARMOUR_MATRIX, RIG_SPLASH_DAMAGE_FACTOR } from '../content/vessels';
import {
  AUTOCANNON, GUN_MOUNT, TORPEDO, DEPTH_CHARGE, TORPEDO_TUBES, PROJECTOR_MAGAZINE,
  TORPEDO_MOUNT, PROJECTOR_MOUNT, TORPEDO_RACK_RELOAD, TORPEDO_LOCK, GUN_LOCK, TORPEDO_INTERCEPT,
  projectorEnvelope,
} from '../content/weapons';
import { STARTING_RESOURCES, ECONOMY, MANIFEST_ITEMS, type UpgradeId } from '../content/economy';
import {
  LULL_GRACE_SECONDS, LULL_RATE, LULL_ROUTE_NEAR_METRES, LULL_ROUTE_MAX,
  DECIDED_ROUTE_MAX, SHIFT_OUTRO_SECONDS,
} from '../content/pacing';
import type { ShiftScript, ShiftBeat, SpawnBeat, Trigger } from '../content/schema';
import { KRAKEN_LINES, SYSTEM_LINES } from '../content/radio';
import { clamp, dirFromHeading, lobVelocity, type Vec3 } from './math';
import {
  MAX_SPAWN_RANGE, DESPAWN_RANGE, SAFE_LINE_METRES,
  RIG_HIT_REPORT_POINT, RIG_HIT_REPORT_SPREAD,
} from './constants';

export interface SimWorldOptions {
  script: ShiftScript;
  resources?: Resources;
  upgrades?: readonly UpgradeId[];
  /** Manifest item ids ordered between shifts; ride in on this shift's supply vessel. */
  pendingManifest?: readonly string[];
}

/**
 * What an acquisition machine needs to know. Both `TORPEDO_LOCK` and `GUN_LOCK` satisfy
 * it; the machine itself is `stepAimLock` and there is only one of it.
 */
interface AimLockTuning {
  acquireSeconds: number;
  coneDeg: number;
  hullWidthFactor: number;
  holdConeFactor: number;
  offTargetDrain: number;
  maxRange: number;
}

/** Per-beat bookkeeping for triggers that wait on something. */
interface BeatState {
  fired: boolean;
  /** Seconds this beat has been waiting for its trigger, for the timeout fallback. */
  waited: number;
}

/** What control says as each phase turns. Silence during the dread is the point. */
export class SimWorld {
  readonly events = new EventQueue();
  readonly vessels: Vessel[] = [];
  readonly projectiles = new ProjectilePool();
  readonly resources: Resources;
  readonly rig: RigState;
  readonly shift: ShiftProgress;

  /** Seconds since the world was created. Drives waves and every timer. */
  time = 0;

  weather: WeatherState;
  waveField: WaveField;

  selectedId: EntityId | null = null;

  /**
   * The torpedo's aim-lock (see TORPEDO_LOCK). The vessel the crosshair is currently
   * building a lock on — or holding one on — and how many seconds of dwell it has.
   * Locked means progress has reached `acquireSeconds`; the torpedo will not launch
   * without it. Deliberately independent of `selectedId`: the gun keeps Q/E, the
   * torpedo locks with the crosshair alone.
   */
  torpedoLockId: EntityId | null = null;
  private torpedoLockProgress = 0;
  /**
   * The autocannon's aim-lock: the hull the crosshair has settled on, and its dwell.
   *
   * Drives the lead pip, and once complete it drives the selection too — see
   * `stepGunLock`. Separate state from the torpedo's because the two weapons are never
   * armed at once and each wants its own cone.
   */
  gunLockId: EntityId | null = null;
  private gunLockProgress = 0;
  /**
   * True while the player has their eye to the optic.
   *
   * A player input like `firing` or `aimDir`, not a rendering detail: the gun only
   * acquires down the scope, so the view is now part of what the simulation is told. It is
   * recorded and replayed like any other input, so determinism is unaffected.
   */
  scoped = false;
  /** True while the player is holding the trigger. */
  firing = false;
  /** Where the player's gun is pointed, as a unit direction. */
  aimDir: Vec3 = { x: 0, y: 0, z: 1 };
  /** Spotlight bearing, or null when off. */
  spotlightBearing: number | null = null;

  private readonly rng: Rng;
  private readonly script: ShiftScript;
  private readonly beats: BeatState[];
  private readonly upgrades: Set<UpgradeId>;
  /**
   * What the rig is fitted with right now, including anything a ship landed this watch.
   * The campaign banks this at shift end so equipment delivered by hull outlives the
   * shift that delivered it.
   */
  get fittedUpgrades(): readonly UpgradeId[] {
    return [...this.upgrades];
  }
  private readonly spawnTags = new Map<string, EntityId[]>();
  /** Ordered goods still waiting to be loaded onto a supply vessel. */
  private pendingManifest: string[];

  /** Weather blend in progress, if a script beat asked for one. */
  private weatherBlend: { from: WeatherState; to: WeatherState; t: number; duration: number } | null = null;

  /**
   * Seconds the `at`-trigger clock has been run forward because the sector was clear.
   *
   * Kept apart from `shift.elapsed` deliberately: the shift's own clock still drives the
   * watchdog and the timer the player reads, so a shift does not silently shorten — only
   * the queue of scheduled arrivals moves up. See `stepLull`.
   */
  private lullSkip = 0;
  /** Seconds since the sector last went quiet; reset the moment a hostile is alive. */
  private lullTime = 0;
  /** Whether anything hostile has drawn breath yet, so the opening is never compressed. */
  private sawHostile = false;
  /** True while the sector is clear and the grace has passed. Read by `stepVessels`. */
  private lullActive = false;
  /** Counting down the beat between the shift being won and the results. */
  private outroTimer = 0;
  /** Rounds fired since the last tracer. */
  private roundsSinceTracer = 0;
  /** Fractional rounds owed, so a 300 rpm gun at 60 Hz fires at the right rate. */
  private fireAccumulator = 0;
  /**
   * The Kraken encounter, when one is running. Null in every other shift.
   *
   * Kept as its own state object rather than smeared across the boss vessel, because the
   * phase machine is pure and unit-tested on its own — the vessel is what the phases move
   * around, not where they live.
   */
  kraken: KrakenState | null = null;

  constructor(opts: SimWorldOptions) {
    this.script = opts.script;
    this.rng = new Rng(hashSeed(opts.script.seed));
    this.upgrades = new Set(opts.upgrades ?? []);
    resetEntityIds();

    this.resources = { ...(opts.resources ?? STARTING_RESOURCES) };
    this.weather = cloneWeather(WEATHER_PRESETS[opts.script.weather]);
    this.waveField = buildWaveField({
      seaState: this.weather.seaState,
      windDir: this.weather.windDir,
      seed: opts.script.seed,
    });

    /*
     * Tube capacity, fixed for the shift: the extra pair is fitted between shifts, not
     * during one. Computed here because the rig initialiser below runs before `this.rig`
     * exists to hold it.
     */
    const tubeCapacity = TORPEDO_TUBES + (this.upgrades.has('torpedoTubes') ? 2 : 0);

    /*
     * The gun is loaded before the watch, not left as the last one finished with it.
     *
     * `resources.ammo` is the magazine's own count and it persists across shifts, so a
     * shift that ended with 785 rounds in the tray opened the next one showing 785 — on a
     * rig with 2400 more in the locker. Reading that as "my ammunition did not carry over"
     * is the correct reading of what the weapon card said; the stock was carrying, the gun
     * simply was not being reloaded in the hours between watches. Every other weapon
     * already re-derives from stock at this point (tubes below, the projector's magazine
     * under it), so the autocannon was the odd one out rather than the rule.
     *
     * Rounds are conserved: whatever the tray gains, the reserve loses.
     */
    const loaded = Math.min(
      AUTOCANNON.magazine,
      this.resources.ammo + this.resources.ammoReserve,
    );
    this.resources.ammoReserve -= Math.max(0, loaded - this.resources.ammo);
    this.resources.ammo = loaded;

    this.rig = {
      integrity: this.resources.integrity,
      alarmTimer: 0,
      magazine: loaded,
      reloadTimer: 0,
      dispersionHeat: 0,
      fireCooldown: 0,
      selectedWeapon: 'autocannon',
      torpedoInterlock: 0,
      torpedoRackTimer: 0,
      torpedoTubes: Math.min(tubeCapacity, this.resources.torpedoes),
      torpedoCapacity: tubeCapacity,
      projectorCooldown: 0,
      projectorSwapTimer: 0,
      projectorMagazine: Math.min(PROJECTOR_MAGAZINE, this.resources.depthCharges),
      fuseDepth: DEPTH_CHARGE.fuseDepths[1] ?? 25,
    };

    this.shift = {
      index: opts.script.index,
      elapsed: 0,
      nextEvent: 0,
      arrivalsScheduled: 0,
      arrivalsSafe: 0,
      arrivalsLost: 0,
      outcome: 'running',
      objective: '',
      earned: 0,
      oilDelivered: 0,
      hostilesDestroyed: 0,
      roundsFired: 0,
      roundsHit: 0,
    };

    this.beats = opts.script.beats.map(() => ({ fired: false, waited: 0 }));
    this.pendingManifest = [...(opts.pendingManifest ?? [])];
  }

  /**
   * How much faster the high-speed screws drive a torpedo, as a multiplier on the whole
   * acceleration curve. Read once per tick by the integrator rather than baked into the
   * weapon's tuning, so the upgrade cannot leak into a save or a replay.
   */
  private get torpedoSpeedScale(): number {
    return this.upgrades.has('torpedoScrews') ? 1.25 : 1;
  }

  /** True once the ordered manifest has been loaded onto a vessel. */
  get manifestDispatched(): boolean {
    return this.pendingManifest.length === 0;
  }

  // --- Public commands ----------------------------------------------------------------

  select(id: EntityId | null): void {
    this.selectedId = id;
  }

  /** Cycle to the next detected contact, hostiles first. */
  cycleSelection(direction: 1 | -1 = 1): void {
    const candidates = this.vessels
      .filter((v) => v.detection !== 'undetected' && v.state !== 'dead')
      .sort((a, b) => rangeToRig(a) - rangeToRig(b));
    if (candidates.length === 0) { this.selectedId = null; return; }
    const idx = candidates.findIndex((v) => v.id === this.selectedId);
    const next = idx < 0
      ? 0
      : (idx + direction + candidates.length) % candidates.length;
    this.selectedId = (candidates[next] as Vessel).id;
  }

  setWeapon(w: WeaponId): void {
    this.rig.selectedWeapon = w;
  }

  /** Aim the gun along a unit direction in world space. */
  setAim(dir: Vec3): void {
    this.aimDir = dir;
  }

  setFiring(on: boolean): void {
    this.firing = on;
  }

  /** Eye to the optic, or back on the sector. The gun only acquires down the scope. */
  setScoped(on: boolean): void {
    this.scoped = on;
  }

  hasUpgrade(id: UpgradeId): boolean {
    return this.upgrades.has(id);
  }

  // --- The tick -----------------------------------------------------------------------

  tick(dt: number): void {
    this.time += dt;
    if (this.shift.outcome !== 'running') return;
    this.shift.elapsed += dt;

    this.stepWeather(dt);
    this.stepLull(dt);
    this.runScript();
    this.stepPlayerWeapons(dt);
    this.stepVessels(dt);
    this.stepKrakenEncounter(dt);
    this.stepProjectilesAndDamage(dt);
    this.stepEconomy(dt);
    this.checkEndConditions(dt);
  }

  // --- Weather ------------------------------------------------------------------------

  private stepWeather(dt: number): void {
    if (!this.weatherBlend) return;
    const b = this.weatherBlend;
    b.t = Math.min(1, b.t + dt / b.duration);
    this.weather = lerpWeather(b.from, b.to, b.t);
    // The wave field is rebuilt only at the end of a blend: rebuilding per tick would
    // change the field under vessels that are already floating on it.
    if (b.t >= 1) {
      this.waveField = buildWaveField({
        seaState: this.weather.seaState,
        windDir: this.weather.windDir,
        seed: this.script.seed,
      });
      this.weatherBlend = null;
    }
  }

  setWeatherPreset(name: WeatherPresetName, overSeconds = 0): void {
    const to = cloneWeather(WEATHER_PRESETS[name]);
    if (overSeconds <= 0) {
      this.weather = to;
      this.waveField = buildWaveField({
        seaState: to.seaState, windDir: to.windDir, seed: this.script.seed,
      });
      this.weatherBlend = null;
      return;
    }
    this.weatherBlend = { from: cloneWeather(this.weather), to, t: 0, duration: overSeconds };
  }

  // --- Shift script -------------------------------------------------------------------

  /**
   * Run the trigger clock on while the sector is clear, so clearing a wave early brings
   * the next one forward instead of buying empty water.
   *
   * Three conditions, and each is load-bearing:
   *
   *  - **A fight has happened.** Every shift opens with no hostiles alive, and the first
   *    minute is scripted approach and radio. Without this the compression would eat the
   *    opening of every shift before the player had done anything to earn it.
   *  - **Nothing hostile is left.** Measured the same way `checkEndConditions` measures
   *    it — a sinking wreck or an outbound hull is presentation, not a fight.
   *  - **Something is actually waiting on the clock.** If the next beat hangs on a
   *    friendly's range instead, there is nothing here to bring forward, and running the
   *    clock would only distort the timeouts.
   *
   * Only `at` triggers read this. The range-gated ambushes stay on the real clock on
   * purpose: they are staged at a defensible distance from the ship they attack, and
   * pulling them forward is precisely the bug that had skiffs engaging a tanker 2 km
   * out, past where the 30 mm connects.
   */
  private stepLull(dt: number): void {
    /*
     * Some shifts are written to be waited through, and their silence is the content
     * rather than a defect in it. See `ShiftScript.pacing`.
     */
    if (this.script.pacing === 'authored') {
      this.lullActive = false;
      return;
    }

    /*
     * A hull the player cannot detect is not a fight.
     *
     * This used to count any live hostile, which is right for everything that floats and
     * wrong for the one that does not: a mini-sub runs in submerged and undetectable, so
     * the sea is empty as far as the player is concerned while the clock is told a fight
     * is under way. Shift 8 opens on exactly that — a lone sub, deliberately, "the thing
     * itself" — and it bought two minutes and eighteen seconds of nothing before first
     * contact, against eight to twenty-two seconds for every shift either side of it.
     *
     * `sawHostile` is deliberately still set below, so the grace period cannot be spent
     * before the player has had anything to shoot at.
     */
    const hostilesLeft = this.vessels.some(
      (v) => v.faction === 'hostile' && v.state !== 'dead'
        && v.state !== 'sinking' && v.state !== 'departing'
        && v.detection !== 'undetected',
    );
    if (hostilesLeft) {
      this.sawHostile = true;
      this.lullTime = 0;
      this.lullActive = false;
      return;
    }
    // The finale runs its own phase clock and submerges between assaults; an empty sea
    // there is the boss holding its breath, not a lull the player has earned.
    if (!this.sawHostile || (this.kraken && this.kraken.phase !== 'gone')) {
      this.lullActive = false;
      return;
    }

    this.lullTime += dt;
    if (this.lullTime <= LULL_GRACE_SECONDS) {
      this.lullActive = false;
      return;
    }
    this.lullActive = true;

    // Only run the trigger clock on when something is actually waiting on it. A beat
    // gated on a friendly's range is not brought forward — that one is handled by
    // compressing the voyage instead, which keeps the ambush at its authored distance.
    const clockTime = this.shift.elapsed + this.lullSkip;
    const waitingOnClock = this.script.beats.some((beat, i) => {
      const state = this.beats[i] as BeatState;
      return !state.fired && 'at' in beat.trigger && beat.trigger.at > clockTime;
    });
    if (waitingOnClock) this.lullSkip += dt * (LULL_RATE - 1);
  }

  private runScript(): void {
    for (let i = 0; i < this.script.beats.length; i++) {
      const state = this.beats[i] as BeatState;
      if (state.fired) continue;
      const beat = this.script.beats[i] as ShiftBeat;
      const verdict = this.evaluateTrigger(beat.trigger, state);
      if (verdict === 'wait') continue;
      /*
       * A cancelled beat is still *spent* — it counts toward `allBeatsFired`, so a shift
       * whose remaining content has been called off can close rather than hang on beats
       * that will never happen. It simply is not applied.
       */
      state.fired = true;
      if (verdict === 'cancel') {
        // Chained triggers must see the wave that never came. A tag registered empty is
        // "cancelled"; a tag that is absent has merely not spawned yet.
        if (beat.kind === 'spawn') this.spawnTags.set(beat.tag, []);
        continue;
      }
      this.applyBeat(beat);
    }
  }

  /**
   * Every waiting trigger also counts down a timeout. This is the rule that makes shifts
   * unwedgeable: if the tanker a beat was waiting on was sunk at 6 km, the beat still
   * fires eventually rather than leaving the player on a silent ocean forever.
   *
   * The timeout is the fallback for a subject that is *slow*, though, and it was doing
   * duty for a subject that is *gone* — which is a different thing and was measurably
   * wrong. Every `whenWithin` gate in the game hangs off a friendly hull's range, so
   * sinking the tanker forty seconds into shift 1 left three ambushes and their radio
   * lines waiting on ranges she could never reach: 600 s, 420 s and 300 s of empty water,
   * then the whole remainder of the shift firing out of authored order, announcing
   * contacts closing on a ship that had been on the bottom for nine minutes. The plan's
   * liveness rule (§E4) names three fallbacks — fire anyway, skip, substitute — and for
   * an ambush staged on a vessel that no longer exists the honest one is *skip*.
   */
  private evaluateTrigger(trigger: Trigger, state: BeatState): 'wait' | 'fire' | 'cancel' {
    if ('at' in trigger) {
      // Against the trigger clock, not the shift clock: `lullSkip` is the time the
      // player bought by clearing the sector early (see stepLull).
      return this.shift.elapsed + this.lullSkip >= trigger.at ? 'fire' : 'wait';
    }

    state.waited += 1 / 60;
    const timedOut = state.waited >= trigger.timeout;

    if ('whenWithin' in trigger) {
      const ids = this.spawnTags.get(trigger.whenWithin.tag);
      // Not spawned yet: the subject is still coming, so wait (or time out).
      if (ids === undefined) return timedOut ? 'fire' : 'wait';
      if (ids.length === 0) return 'cancel';
      // Checked before the range test: a hull that is burning and settling can drift
      // across a gate it was never going to reach under way, and staging an ambush on it
      // is the same fiction as staging one on a wreck.
      const reachable = ids.some((id) => {
        const v = findVessel(this.vessels, id);
        return v && v.state !== 'dead' && v.state !== 'sinking';
      });
      if (!reachable) return 'cancel';
      const hit = ids.some((id) => {
        const v = findVessel(this.vessels, id);
        return v && v.state !== 'dead' && rangeToRig(v) <= trigger.whenWithin.km * 1000;
      });
      return hit || timedOut ? 'fire' : 'wait';
    }

    if ('whenResolved' in trigger) {
      const ids = this.spawnTags.get(trigger.whenResolved.tag);
      if (ids === undefined) return timedOut ? 'fire' : 'wait';
      // The wave this answers was itself called off — there is nothing to answer.
      if (ids.length === 0) return 'cancel';
      const allDone = ids.every((id) => {
        const v = findVessel(this.vessels, id);
        return !v || v.state === 'dead' || v.state === 'sinking' || v.state === 'departing';
      });
      return allDone || timedOut ? 'fire' : 'wait';
    }

    return 'wait';
  }

  private applyBeat(beat: ShiftBeat): void {
    switch (beat.kind) {
      case 'spawn': {
        const ids: EntityId[] = [];
        for (let n = 0; n < beat.count; n++) {
          const spread = beat.spreadDeg ?? 0;
          const offset = beat.count > 1 ? (n - (beat.count - 1) / 2) * spread : 0;
          const range = Math.min(beat.rangeKm * 1000, MAX_SPAWN_RANGE);
          let cargo = beat.cargo ? cargoFor(beat.cargo) : undefined;
          // The first supply vessel of the shift carries whatever was ordered.
          if (beat.vessel === 'supply' && this.pendingManifest.length > 0) {
            cargo = mergeManifest(cargo, this.pendingManifest);
            this.pendingManifest = [];
          }
          const v = spawnAtBearing(beat.vessel, beat.bearingDeg + offset, range, cargo);
          /*
           * Limbs of one animal, not a rank of posts.
           *
           * The bearing fan above is right for boats coming in abreast and wrong for a
           * creature: it puts every limb at the same range with the same heading, which
           * reads as a row of identical stakes. A tentacle group is redistributed around
           * the centre of that fan instead, so the far ones stand behind the near ones.
           */
          if (beat.vessel === 'tentacle') {
            /*
             * Limbs come up around something, and they come up for a reason.
             *
             * The ring used to be struck around the beat's own bearing point, which put
             * three limbs in open water with nothing in reach: they were assigned no
             * target, so they crawled at 6 m/s toward a platform two kilometres away and
             * arrived after the shift was over. Adam, on shift 10: "the tentacles just
             * appear in the middle of the sea and they don't endanger any friendly
             * vessel — so there's no apparent reason to shoot them."
             *
             * They surface around the hull they are hunting now, and name it. If there is
             * no friendly in the water they fall back to the authored bearing and come
             * for the rig, which is the finale's case and already reads.
             */
            const victim = this.vessels.find(
              (other) => other.faction === 'friendly'
                && other.state === 'inbound'
                && !other.safe,
            ) ?? null;
            const centre = victim
              ? { x: victim.pos.x, z: victim.pos.z }
              : (() => {
                const dir = dirFromHeading((beat.bearingDeg * Math.PI) / 180);
                return { x: dir.x * range, z: dir.z * range };
              })();
            const spot = tentacleRingPlacement(n, beat.count, centre.x, centre.z, this.rng);
            v.pos.x = spot.x;
            v.pos.z = spot.z;
            v.heading = spot.heading;
            v.lastKnownX = spot.x;
            v.lastKnownZ = spot.z;
            if (victim) v.targetId = victim.id;
          }
          v.spawnedAt = this.time;
          if (beat.mission !== undefined) v.mission = beat.mission;
          this.vessels.push(v);
          ids.push(v.id);
          if (v.faction === 'friendly') this.shift.arrivalsScheduled++;
        }
        this.spawnTags.set(beat.tag, ids);
        break;
      }
      case 'objective':
        this.shift.objective = beat.text;
        this.events.emit({ type: 'Objective', text: beat.text });
        break;
      case 'radio':
        this.events.emit({
          type: 'Radio',
          speaker: beat.speaker,
          text: beat.text,
          ...(beat.read !== undefined ? { read: beat.read } : {}),
        });
        break;
      case 'weather':
        this.setWeatherPreset(beat.preset, beat.overSeconds);
        break;
    }
  }

  // --- Player weapons -----------------------------------------------------------------

  private stepPlayerWeapons(dt: number): void {
    const rig = this.rig;
    rig.fireCooldown = Math.max(0, rig.fireCooldown - dt);
    rig.torpedoInterlock = Math.max(0, rig.torpedoInterlock - dt);
    rig.projectorCooldown = Math.max(0, rig.projectorCooldown - dt);
    this.stepTorpedoRacks(dt);
    this.stepProjectorSwap(dt);
    this.stepTorpedoLock(dt);
    this.stepGunLock(dt);

    // Dispersion recovers when the trigger is released.
    if (!this.firing) {
      rig.dispersionHeat *= Math.pow(AUTOCANNON.dispersionRecovery, dt);
    }

    if (rig.reloadTimer > 0) {
      rig.reloadTimer -= dt;
      if (rig.reloadTimer <= 0) {
        rig.magazine += reloadMagazine(this.resources, AUTOCANNON.magazine, rig.magazine);
        this.resources.ammo = rig.magazine;
        this.events.emit({ type: 'WeaponState', weapon: 'autocannon', state: 'ready' });
      }
      return;
    }

    if (rig.selectedWeapon === 'torpedo') {
      this.fireAccumulator = 0;
      if (this.firing) this.launchTorpedo();
      return;
    }

    if (rig.selectedWeapon === 'depthCharge') {
      this.fireAccumulator = 0;
      if (this.firing) this.lobDepthCharge();
      return;
    }

    if (!this.firing) {
      this.fireAccumulator = 0;
      return;
    }

    if (rig.magazine <= 0) {
      if (this.resources.ammoReserve > 0) {
        // Autoloader halves the reload; a thin crew removes the loading team entirely.
        const crewPenalty = this.resources.crew < ECONOMY.crewReloadThreshold ? 1.6 : 1;
        rig.reloadTimer = AUTOCANNON.reloadSeconds
          * (this.upgrades.has('autoloader') ? 0.5 : 1) * crewPenalty;
        this.events.emit({ type: 'WeaponState', weapon: 'autocannon', state: 'reloading' });
      } else {
        this.events.emit({ type: 'WeaponState', weapon: 'autocannon', state: 'empty' });
      }
      return;
    }

    // Fire at the tuned rate, carrying the fractional remainder between ticks so the
    // rounds-per-minute figure is honest rather than quantised to the tick.
    const interval = 60 / AUTOCANNON.rpm;
    this.fireAccumulator += dt;
    while (this.fireAccumulator >= interval && rig.magazine > 0) {
      this.fireAccumulator -= interval;
      this.fireRound();
    }
  }

  /**
   * Bring spent tubes back on line, one at a time.
   *
   * Sequential rather than parallel: two tubes do not reload simultaneously because one
   * crew works the racks. It is also what makes the second salvo a genuinely different
   * proposition from the first.
   */
  private stepTorpedoRacks(dt: number): void {
    const rig = this.rig;
    if (rig.torpedoTubes >= rig.torpedoCapacity) return;
    if (this.resources.torpedoes <= rig.torpedoTubes) return;
    rig.torpedoRackTimer -= dt;
    if (rig.torpedoRackTimer <= 0) {
      rig.torpedoTubes++;
      rig.torpedoRackTimer = TORPEDO_RACK_RELOAD;
      this.events.emit({ type: 'WeaponState', weapon: 'torpedo', state: 'ready' });
    }
  }

  private stepProjectorSwap(dt: number): void {
    const rig = this.rig;
    if (rig.projectorSwapTimer <= 0) return;
    rig.projectorSwapTimer -= dt;
    if (rig.projectorSwapTimer <= 0) {
      rig.projectorMagazine = Math.min(PROJECTOR_MAGAZINE, this.resources.depthCharges);
      this.events.emit({ type: 'WeaponState', weapon: 'depthCharge', state: 'ready' });
    }
  }

  /** Fuse-depth selector on the weapon card. Snaps to the tuned set. */
  setFuseDepth(metres: number): void {
    let best = DEPTH_CHARGE.fuseDepths[0] ?? 25;
    for (const d of DEPTH_CHARGE.fuseDepths) {
      if (Math.abs(d - metres) < Math.abs(best - metres)) best = d;
    }
    this.rig.fuseDepth = best;
  }

  /** True when the torpedo holds a completed lock on a live target. */
  get torpedoLocked(): boolean {
    return this.torpedoLockId !== null
      && this.torpedoLockProgress >= TORPEDO_LOCK.acquireSeconds;
  }

  /** The gun has a hull: the lead pip is real and the selection has followed it. */
  get gunLocked(): boolean {
    return this.gunLockId !== null && this.gunLockProgress >= GUN_LOCK.acquireSeconds;
  }

  get gunLockFraction(): number {
    return Math.min(1, this.gunLockProgress / GUN_LOCK.acquireSeconds);
  }

  /** Lock progress, 0..1 — what the scope's lock ring draws. */
  get torpedoLockFraction(): number {
    return Math.min(1, this.torpedoLockProgress / TORPEDO_LOCK.acquireSeconds);
  }

  /**
   * The torpedo's aim-lock: dwell on a hostile hull to earn the shot (TORPEDO_LOCK).
   *
   * The cone is deliberately generous — the hull's own angular half-width plus a fixed
   * margin — because the test is "is the player deliberately on this ship", not "is the
   * crosshair pixel-perfect". Wandering off drains progress a few times faster than
   * dwell builds it rather than zeroing it, so a wobble at 8× is survivable but a real
   * loss means starting the 1.2 s over. Once locked, the lock survives in a wider cone.
   */
  private stepTorpedoLock(dt: number): void {
    if (this.rig.selectedWeapon !== 'torpedo') {
      this.torpedoLockId = null;
      this.torpedoLockProgress = 0;
      return;
    }
    const next = this.stepAimLock(
      this.torpedoLockId, this.torpedoLockProgress, dt, TORPEDO_LOCK, this.torpedoLocked,
    );
    this.torpedoLockId = next.id;
    this.torpedoLockProgress = next.progress;
  }

  /**
   * The autocannon's acquisition — the same machine, its own tuning.
   *
   * Only down the optic. In the wide view the crosshair is a survey instrument that sweeps
   * the whole sector, and letting it acquire there would drag the selection, the target
   * panel and the radar highlight around with every glance.
   *
   * Selection follows the pip once it comes in, on Adam's direction: the alternative is a
   * target panel describing one hull while the pip sits on another, which is worse than
   * either behaviour on its own. Q/E still moves it — until the crosshair acquires again.
   */
  private stepGunLock(dt: number): void {
    if (this.rig.selectedWeapon !== 'autocannon' || !this.scoped) {
      this.gunLockId = null;
      this.gunLockProgress = 0;
      return;
    }
    const next = this.stepAimLock(
      this.gunLockId, this.gunLockProgress, dt, GUN_LOCK, this.gunLocked,
    );
    this.gunLockId = next.id;
    this.gunLockProgress = next.progress;
    if (this.gunLocked && this.gunLockId !== null) this.selectedId = this.gunLockId;
  }

  /**
   * One acquisition machine, shared by the tube and the gun.
   *
   * Both weapons answer the same question — is the player deliberately pointing at this
   * hull — and they answered it with two copies of the same twenty-five lines for about an
   * hour. The differences that matter are all numbers, so they live in the tuning blocks
   * (`TORPEDO_LOCK`, `GUN_LOCK`) and this stays the single place the rule is written.
   */
  private stepAimLock(
    lockId: EntityId | null,
    lockProgress: number,
    dt: number,
    tuning: AimLockTuning,
    alreadyLocked: boolean,
  ): { id: EntityId | null; progress: number } {
    const flat = Math.hypot(this.aimDir.x, this.aimDir.z);
    const aimBearing = flat < 1e-4 ? null : Math.atan2(this.aimDir.x, this.aimDir.z);

    const coneFor = (v: Vessel, widen: number): number => {
      const range = Math.max(1, rangeToRig(v));
      /*
       * The silhouette the hull actually presents, not its length.
       *
       * This used to be `length * 0.5` whatever the aspect, which made the cone as wide
       * for a skiff coming bow-on as for one lying broadside — five times wider than
       * anything visible. Combined with the fixed margin it meant pointing the scope in
       * a hull's general direction locked it, and with two hostiles on close bearings
       * the player could not tell which one the tube had chosen.
       *
       * Projected width of a hull of length L and beam B seen at aspect a is
       * |L·sin a| + |B·cos a|: L broadside, B bow-on, and the right thing in between.
       */
      const spec = vesselSpec(v.kind);
      const aspect = v.heading - Math.atan2(v.pos.x, v.pos.z);
      const presented = Math.abs(spec.length * Math.sin(aspect))
        + Math.abs(spec.beam * Math.cos(aspect));
      const halfWidth = Math.atan2(
        presented * 0.5 * tuning.hullWidthFactor, range,
      );
      return (halfWidth + (tuning.coneDeg * Math.PI) / 180) * widen;
    };
    const errorTo = (v: Vessel): number => {
      if (aimBearing === null) return Math.PI;
      let d = (Math.atan2(v.pos.x, v.pos.z) - aimBearing) % (Math.PI * 2);
      if (d > Math.PI) d -= Math.PI * 2;
      if (d < -Math.PI) d += Math.PI * 2;
      return Math.abs(d);
    };
    // Anything hostile and visible can be locked — including the creatures; only the
    // outcome roll treats them specially. Terminal states cannot hold a lock. Friendlies
    // are absent by construction, which is what keeps aiming furniture off the escort.
    const lockable = (v: Vessel): boolean =>
      v.faction === 'hostile'
      && v.detection !== 'undetected'
      && v.state !== 'dead' && v.state !== 'sinking' && v.state !== 'departing'
      && rangeToRig(v) <= tuning.maxRange;

    // Hold the current target while it stays inside its cone (wider once locked)...
    const current = lockId === null
      ? undefined
      : this.vessels.find((v) => v.id === lockId);
    if (current && lockable(current)
      && errorTo(current)
        <= coneFor(current, alreadyLocked ? tuning.holdConeFactor : 1)) {
      return {
        id: lockId,
        progress: Math.min(tuning.acquireSeconds, lockProgress + dt),
      };
    }

    // ...otherwise the hull nearest the crosshair becomes the new candidate...
    let best: Vessel | undefined;
    let bestError = Infinity;
    for (const v of this.vessels) {
      if (!lockable(v)) continue;
      const err = errorTo(v);
      if (err <= coneFor(v, 1) && err < bestError) { best = v; bestError = err; }
    }
    if (best) return { id: best.id, progress: dt };

    // ...and with nothing on the wire, progress drains rather than snapping to zero,
    // keeping its id so re-centring on the same hull resumes what is left.
    const drained = lockProgress - dt * tuning.offTargetDrain;
    return drained <= 0 ? { id: null, progress: 0 } : { id: lockId, progress: drained };
  }

  /**
   * Launch one torpedo along the current aim bearing.
   *
   * No lock, no launch (Adam's redesign — the lock is the trigger interlock). The fish
   * is soft-locked to the locked hull and never re-acquires: the gyro corrects a
   * bearing, it does not hunt. Aim is taken in the horizontal plane only — a torpedo
   * cannot be aimed up or down, and letting the scope's elevation leak into the launch
   * would send it into the seabed or out of the water.
   */
  private launchTorpedo(): boolean {
    const rig = this.rig;
    if (rig.torpedoInterlock > 0 || rig.torpedoTubes <= 0) return false;
    if (this.resources.torpedoes <= 0) {
      this.events.emit({ type: 'WeaponState', weapon: 'torpedo', state: 'empty' });
      return false;
    }

    const target = this.torpedoLocked
      ? this.vessels.find((v) => v.id === this.torpedoLockId)
      : undefined;
    if (!target) {
      this.events.emit({ type: 'WeaponState', weapon: 'torpedo', state: 'noLock' });
      return false;
    }

    const flat = Math.hypot(this.aimDir.x, this.aimDir.z);
    if (flat < 1e-4) return false;
    const dir = { x: this.aimDir.x / flat, y: 0, z: this.aimDir.z / flat };

    /*
     * The fish's fate is rolled here, at launch (TORPEDO_LOCK): range sets the odds,
     * speedboats cap them, and divers and the boss are never rolled at all — their
     * escapes stay physical. The roll is invisible until the last seconds: a doomed
     * fish steers true, a missing one falls just astern.
     */
    const range = Math.hypot(target.pos.x - TORPEDO_MOUNT.x, target.pos.z - TORPEDO_MOUNT.z);
    let rigged: 'hit' | 'miss' | null = null;
    if (!(TORPEDO_LOCK.unriggable as readonly string[]).includes(target.kind)) {
      let chance = torpedoHitChance(range);
      if ((TORPEDO_LOCK.speedboats as readonly string[]).includes(target.kind)) {
        chance = Math.min(chance, TORPEDO_LOCK.speedboatCap);
      }
      rigged = this.rng.next() < chance ? 'hit' : 'miss';
    }

    const muzzle: Vec3 = { x: TORPEDO_MOUNT.x, y: TORPEDO_MOUNT.y, z: TORPEDO_MOUNT.z };
    const v = TORPEDO.launchSpeed;
    const p = this.projectiles.spawn(
      'torpedo',
      muzzle,
      // A slight downward component: it is pushed out of the tube, not thrown.
      { x: dir.x * v, y: -1.2, z: dir.z * v },
      {
        hostile: false,
        damage: TORPEDO.damage,
        ownerId: null,
        targetId: target.id,
        rigged,
      },
    );
    if (!p) return false;

    rig.torpedoTubes--;
    this.resources.torpedoes--;
    rig.torpedoInterlock = TORPEDO.tubeCycleSeconds;
    if (rig.torpedoRackTimer <= 0) rig.torpedoRackTimer = TORPEDO_RACK_RELOAD;
    this.events.emit({
      type: 'WeaponFired', weapon: 'torpedo', pos: muzzle, dir, hostile: false,
    });
    return true;
  }

  /**
   * Lob one depth charge at the aim point on the water.
   *
   * The arc is computed for the player: a mortar's elevation is not a thing anyone can
   * usefully eyeball, and plan D3 specifies the range envelope is what the player manages,
   * not the trajectory. Outside that envelope the shot is refused with a warning rather
   * than thrown short — never a silent dud.
   */
  private lobDepthCharge(): boolean {
    const rig = this.rig;
    if (rig.projectorCooldown > 0 || rig.projectorSwapTimer > 0) return false;
    if (rig.projectorMagazine <= 0) {
      if (this.resources.depthCharges > 0) {
        rig.projectorSwapTimer = DEPTH_CHARGE.magSwapSeconds;
        this.events.emit({ type: 'WeaponState', weapon: 'depthCharge', state: 'reloading' });
      } else {
        this.events.emit({ type: 'WeaponState', weapon: 'depthCharge', state: 'empty' });
      }
      return false;
    }

    const aimPoint = this.aimPointOnWater();
    if (!aimPoint) return false;
    const range = Math.hypot(aimPoint.x - PROJECTOR_MOUNT.x, aimPoint.z - PROJECTOR_MOUNT.z);
    const envelope = projectorEnvelope(this.upgrades.has('projectorRails'));
    if (range < envelope.minRange || range > envelope.maxRange) {
      this.events.emit({ type: 'WeaponState', weapon: 'depthCharge', state: 'outOfEnvelope' });
      return false;
    }

    const launch = lobVelocity(PROJECTOR_MOUNT, aimPoint, envelope.launchSpeed);
    const p = this.projectiles.spawn(
      'depthCharge',
      { x: PROJECTOR_MOUNT.x, y: PROJECTOR_MOUNT.y, z: PROJECTOR_MOUNT.z },
      launch,
      {
        hostile: false,
        damage: DEPTH_CHARGE.damage,
        ownerId: null,
        fuseDepth: rig.fuseDepth,
      },
    );
    if (!p) return false;

    rig.projectorMagazine--;
    this.resources.depthCharges--;
    rig.projectorCooldown = DEPTH_CHARGE.cycleSeconds;
    if (rig.projectorMagazine <= 0 && this.resources.depthCharges > 0) {
      rig.projectorSwapTimer = DEPTH_CHARGE.magSwapSeconds;
    }
    this.events.emit({
      type: 'WeaponFired',
      weapon: 'depthCharge',
      pos: { x: PROJECTOR_MOUNT.x, y: PROJECTOR_MOUNT.y, z: PROJECTOR_MOUNT.z },
      dir: this.aimDir,
      hostile: false,
    });
    return true;
  }

  /** Where the aim ray meets the sea, or null when it points at or above the horizon. */
  private aimPointOnWater(): Vec3 | null {
    const d = this.aimDir;
    if (d.y >= -1e-3) return null;
    const t = PROJECTOR_MOUNT.y / -d.y;
    return { x: PROJECTOR_MOUNT.x + d.x * t, y: 0, z: PROJECTOR_MOUNT.z + d.z * t };
  }

  /**
   * The cone `fireRound` samples, in one place so the snapshot can report the same
   * number the gun actually uses — the scope draws this ring, and a drawn spread that
   * differed from the sampled one would be a lie on the glass.
   */
  private dispersionCone(): number {
    const upgradeFactor = this.upgrades.has('rifledBarrel') ? 0.7 : 1;
    // The stabiliser halves what the sea does to the aim — the only part of the cone
    // that is weather rather than the gun itself.
    const weatherFactor = this.upgrades.has('stabiliser') ? 0.25 : 0.5;
    return (AUTOCANNON.dispersionBase + this.rig.dispersionHeat * 0.004) * upgradeFactor
      + aimDisturbance(this.weather) * weatherFactor;
  }

  private fireRound(): void {
    const rig = this.rig;
    rig.magazine--;
    this.resources.ammo = rig.magazine;
    this.shift.roundsFired++;

    // Dispersion: a base cone that opens with sustained fire and with the sea state.
    rig.dispersionHeat = Math.min(1, rig.dispersionHeat + AUTOCANNON.dispersionGrowth * 6);
    const cone = this.dispersionCone();

    // Sample the cone with a seeded polar offset — round, not square.
    const a = this.rng.next() * Math.PI * 2;
    const r = Math.sqrt(this.rng.next()) * cone;
    const dir = perturb(this.aimDir, Math.cos(a) * r, Math.sin(a) * r);

    this.roundsSinceTracer++;
    const tracer = this.roundsSinceTracer >= AUTOCANNON.tracerEvery;
    if (tracer) this.roundsSinceTracer = 0;

    const muzzle: Vec3 = { x: GUN_MOUNT.x, y: GUN_MOUNT.y, z: GUN_MOUNT.z };
    const v = AUTOCANNON.muzzleVelocity;
    this.projectiles.spawn(
      'shell',
      muzzle,
      { x: dir.x * v, y: dir.y * v, z: dir.z * v },
      { hostile: false, damage: AUTOCANNON.damage, tracer, ownerId: null },
    );

    this.events.emit({
      type: 'WeaponFired', weapon: 'autocannon', pos: muzzle, dir, hostile: false,
    });
  }

  // --- Vessels ------------------------------------------------------------------------

  private stepVessels(dt: number): void {
    const aiCtx: AiContext = {
      vessels: this.vessels,
      time: this.time,
      dt,
      rng: this.rng,
      fire: (shooter, targetId, aimX, aimY, aimZ) => this.hostileFire(shooter, targetId, aimX, aimY, aimZ),
    };

    // Once every scheduled arrival has resolved, the hostiles have nothing left to
    // attack. Left alone they orbit the rig indefinitely and the shift can only end on
    // the watchdog — twenty minutes of an empty sea. They withdraw instead, which is both
    // what a raider would actually do and what lets a shift close on its own terms.
    const arrivalsResolved = this.shift.arrivalsScheduled > 0
      && this.shift.arrivalsSafe + this.shift.arrivalsLost >= this.shift.arrivalsScheduled;
    const scriptSpent = this.beats.every((b) => b.fired);
    const withdraw = arrivalsResolved && scriptSpent;

    for (const v of this.vessels) {
      if (v.state === 'dead') continue;
      // Catch anything emptied by a path that does not run through resolveHullHit.
      this.killIfDead(v);

      const previousDetection = updateDetection(v, this.weather, dt, {
        hasLongRadar: this.upgrades.has('longRadar'),
        hasThermal: this.upgrades.has('thermal'),
        hasHydrophone: this.upgrades.has('hydrophone'),
        spotlightBearing: this.spotlightBearing,
      });

      if (previousDetection === 'undetected' && v.detection !== 'undetected') {
        this.events.emit({
          type: 'ContactNew', id: v.id, kind: v.kind, hostile: v.faction === 'hostile',
        });
      }
      if (previousDetection !== 'identified' && v.detection === 'identified') {
        this.events.emit({ type: 'ContactIdentified', id: v.id, kind: v.kind });
      }

      if (v.faction === 'hostile') {
        if (withdraw && v.state !== 'sinking') {
          this.withdrawHostile(v);
        } else {
          stepHostileAi(v, aiCtx);
        }
      } else if (v.state !== 'sinking') {
        const result = stepFriendlyApproach(v, dt, ECONOMY.transferSeconds);
        /*
         * Everything that matters happens at the Safe Line, not at the berth.
         *
         * The arrival is counted, the manifest lands and the hull leaves the fight the
         * moment she crosses — which is what lets a resupply actually resupply the
         * battle it arrived in, instead of unloading two minutes after the last hostile
         * was already dead. The docking below is theatre from here on.
         */
        if (result === 'safe') {
          this.events.emit({ type: 'ShipSafe', id: v.id, kind: v.kind, pos: { ...v.pos } });
          const summary = v.cargo
            ? applyCargo(this.resources, v.cargo, this.shift).summary
            : 'nothing of value';
          /*
           * Hardware is fitted the moment it crosses, like every other part of a
           * manifest — the whole point of the Safe Line is that a resupply resupplies
           * the fight it arrived in. Adding it here makes it live for the rest of this
           * watch; the event is what carries it into the save so it survives the shift.
           */
          if (v.cargo?.upgrade && !this.upgrades.has(v.cargo.upgrade)) {
            this.upgrades.add(v.cargo.upgrade);
            this.events.emit({ type: 'UpgradeFitted', upgrade: v.cargo.upgrade });
          }
          this.events.emit({ type: 'CargoDelivered', id: v.id, summary });
          if (!v.resolved) { v.resolved = true; this.shift.arrivalsSafe++; }
        }
      }

      /*
       * Anything under fire or damaged drops to real time immediately.
       *
       * The range clause that used to sit here duplicated `routeTimeScale`'s own near
       * threshold, which is what made a lull unable to compress anything: it forced
       * `engaged` true inside 5 km and short-circuited the scale before the threshold
       * was consulted. The scale owns that decision now, and a lull lowers where it
       * lands — never below an ambush trigger, so a fight is always at real time.
       */
      /*
       * "Engaged" has to mean *under threat now*, not *hurt at some point*.
       *
       * `hp < maxHp` never becomes false again, so a tanker that took a single round in
       * the opening minute was pinned at real time for the whole rest of the shift — the
       * slowest thing on an ocean with nothing left in it. Measured, that one clause was
       * most of the end-of-shift dead air: a hull sitting at ×1 two and a half kilometres
       * out for three minutes while the player watched.
       *
       * A lull means no hostile is alive to threaten anything, so a damaged hull is in no
       * more danger than an untouched one and may compress like any other. The moment a
       * hostile appears the lull ends and she is back on her own speed.
       */
      const engaged = v.state === 'attacking' || (v.hp < v.maxHp && !this.lullActive);
      /*
       * Two compressions, and what separates them is whether anything can still happen.
       *
       * While the script is live a lull only closes an approach down to
       * `LULL_ROUTE_NEAR_METRES`, because a wave staged inside that would land where the
       * gun cannot answer it. Once every beat has fired there is no wave left to stage,
       * so the floor drops to the Safe Line itself and the convoy's last stretch closes
       * at `DECIDED_ROUTE_MAX` — the difference between a shift ending in half a minute
       * and the three to four minutes of watching a hull steam that this replaced.
       *
       * Friendlies only, and only until they are safe. A withdrawing raider hauled at ×12
       * across the near water would read as a glitch, and the berthing is the one part of
       * the approach the player is meant to watch at its own speed.
       */
      let nearMetres: number | undefined;
      let maxScale: number | undefined;
      if (v.faction === 'friendly' && !v.safe && this.lullActive && scriptSpent) {
        nearMetres = SAFE_LINE_METRES;
        maxScale = DECIDED_ROUTE_MAX;
      } else if (this.lullActive) {
        nearMetres = LULL_ROUTE_NEAR_METRES;
        maxScale = LULL_ROUTE_MAX;
      }
      stepVesselMotion(v, this.waveField, this.time, dt, this.rng, engaged, nearMetres, maxScale);
    }

    applySeparation(this.vessels);

    // Reap the dead. Splice from the back so indices stay valid.
    for (let i = this.vessels.length - 1; i >= 0; i--) {
      const v = this.vessels[i] as Vessel;
      if (v.state !== 'dead') continue;
      if (this.selectedId === v.id) this.selectedId = null;
      this.vessels.splice(i, 1);
    }
  }

  /** Break off and run for open sea; despawn once clear. */
  private withdrawHostile(v: Vessel): void {
    if (v.state !== 'departing') {
      v.state = 'departing';
      v.targetId = null;
    }
    const bearing = Math.atan2(v.pos.x, v.pos.z);
    v.goalX = Math.sin(bearing) * 14000;
    v.goalZ = Math.cos(bearing) * 14000;
    v.speed = vesselSpec(v.kind).speed;
    if (rangeToRig(v) > DESPAWN_RANGE) v.state = 'dead';
  }

  /**
   * Drive the Kraken encounter, if this shift has one.
   *
   * The world's job here is only to translate between the phase machine and the entity
   * store: spawn the limbs it asks for, tell it what the player has landed, and charge
   * integrity for the attacks it makes. Every decision about what happens next is the
   * machine's.
   */
  private stepKrakenEncounter(dt: number): void {
    const boss = this.vessels.find((v) => v.kind === 'kraken' && v.state !== 'dead') ?? null;
    if (!boss && !this.kraken) return;
    if (!this.kraken) this.kraken = createKrakenState();
    const state = this.kraken;

    const tentacles = this.vessels.filter(
      (v) => v.kind === 'tentacle' && v.state !== 'dead' && v.state !== 'sinking',
    ).length;

    stepKraken(state, boss, tentacles, this.rng, dt);

    /*
     * Limbs break the surface as a ragged wave, not six objects created inside 100 ms.
     *
     * The phase clock is deterministic and already resets on entry. Deriving the next
     * emergence from the number already spawned avoids another timer in saved/replayed
     * state while still making the cadence exact.
     */
    const tentaclesSpawned = KRAKEN.tentacleCount - state.tentaclesToSpawn;
    const nextTentacleAt = tentaclesSpawned * KRAKEN.tentacleEmergenceIntervalSeconds;
    if (
      state.tentaclesToSpawn > 0
      && state.phase === 'tentacles'
      && state.timer >= nextTentacleAt
    ) {
      /*
       * The final assault comes up in front of the platform, on the creature's own
       * bearing — the limbs are its reach ahead of it, not a separate raid. Every one is
       * for the rig (targetId null): the range fight is over and there is no convoy left
       * to matter; what it wants now is the structure the player is standing on.
       */
      const index = KRAKEN.tentacleCount - state.tentaclesToSpawn;
      const spot = assaultRingPlacement(index, KRAKEN.tentacleCount, this.rng);
      const limb = createVessel('tentacle', spot.x, spot.z, spot.heading);
      limb.targetId = null;
      limb.spawnedAt = this.time;
      this.vessels.push(limb);
      state.tentaclesToSpawn--;
    }

    if (state.justChanged || state.slammed) {
      this.events.emit({
        type: 'KrakenPhase',
        phase: state.phase,
        pos: boss ? { ...boss.pos } : { x: 0, y: 0, z: 0 },
        slam: state.slammed,
      });
    }

    // A wave slam the player did not prevent.
    if (state.slammed) {
      const casualties = applyRigDamage(
        this.resources, KRAKEN.slamIntegrity,
        this.upgrades.has('blastPlating'), this.rng.next(),
      );
      this.rig.integrity = this.resources.integrity;
      this.rig.alarmTimer = 8;
      this.events.emit({
        type: 'RigHit',
        pos: boss ? { ...boss.pos } : { x: 0, y: 0, z: 0 },
        damage: KRAKEN.slamIntegrity,
        casualties,
        cause: 'creature',
      });
    }

    /*
     * No separate continuous drain during the assault. Each limb already charges the rig
     * through its own grapple in `hostileFire`, and having both meant integrity fell
     * twice as fast as either number suggested — 100 to 0 in fifty seconds, before a
     * player could plausibly sever six limbs. One mechanic, one cost.
     *
     * The stare is the exception, and deliberately: while the head is up and the eye is
     * on the player, the creature is leaning on the structure. The bleed is what makes
     * the shot a decision rather than a cutscene prompt — hesitate and it costs, freeze
     * entirely and the rig is lost. Adam chose this over a stare that waits politely.
     */
    if (state.phase === 'rise') {
      this.resources.integrity = Math.max(
        0, this.resources.integrity - KRAKEN.stareIntegrityPerSecond * dt,
      );
      this.rig.integrity = this.resources.integrity;
      if (this.rig.alarmTimer <= 0) this.rig.alarmTimer = 8;
    }

    if (state.justChanged) {
      const line = KRAKEN_LINES[state.phase];
      if (line) {
        this.events.emit({
          type: 'Radio', speaker: 'RIG CONTROL', text: line.text, read: line.read,
        });
      }
    }

    // The kill: it slides under and the shift is won.
    if (state.phase === 'gone' && boss) {
      boss.hp = 0;
      this.killIfDead(boss);
    }
  }

  private hostileFire(
    shooter: Vessel, targetId: EntityId | null, aimX: number, aimY: number, aimZ: number,
  ): void {
    const spec = vesselSpec(shooter.kind);
    const from: Vec3 = { x: shooter.pos.x, y: shooter.pos.y + 2.5, z: shooter.pos.z };
    const dx = aimX - from.x;
    const dy = aimY - from.y;
    const dz = aimZ - from.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-3) return;

    /*
     * A tentacle does not shoot — it is already holding the hull. Damage is applied
     * directly rather than through a projectile, because a grapple has no travel time
     * and modelling one as a bullet fired from a metre away would look absurd.
     */
    if (shooter.kind === 'tentacle') {
      const victim = targetId === null ? null : this.vessels.find((v) => v.id === targetId);
      if (victim && victim.state !== 'dead' && !victim.safe) {
        victim.hp -= spec.weaponDamage;
        this.events.emit({
          type: 'ImpactHull',
          pos: { x: victim.pos.x, y: victim.pos.y + 3, z: victim.pos.z },
          kind: 'rpg',
          targetId: victim.id,
          damage: spec.weaponDamage,
          ricochet: false,
        });
        this.killIfDead(victim);
      } else {
        /*
         * Nothing to take: it is pulling on the rig legs instead.
         *
         * A small fraction of its anti-ship damage, and deliberately so. Integrity is
         * absolute — one damage point is one percent of the platform — so six limbs at
         * their full 22 per grapple would flatten the rig in twenty seconds and make the
         * Maw a timer rather than a fight. Against a hull they are lethal; against
         * eight thousand tonnes of steel jacket they are a slow, frightening drain.
         */
        const casualties = applyRigDamage(
          this.resources, spec.weaponDamage * KRAKEN.tentacleRigDamageFactor,
          this.upgrades.has('blastPlating'), this.rng.next(),
        );
        this.rig.integrity = this.resources.integrity;
        this.rig.alarmTimer = 6;
        this.events.emit({
          type: 'RigHit', pos: { ...shooter.pos }, damage: spec.weaponDamage * 0.35, casualties,
          cause: 'creature',
        });
      }
      return;
    }

    // The corvette shares the rocket's ballistics: her volleys leave at 2.9 km, and an
    // RPG's 180 m/s with drag never covered that distance with anything left to land.
    const kind = shooter.kind === 'fac' || shooter.kind === 'corvette' ? 'rocket'
      : shooter.kind === 'minisub' ? 'torpedo' : 'rpg';
    const speed = kind === 'rocket' ? 240 : kind === 'torpedo' ? 5 : 180;

    // Lob slightly so incoming fire arcs visibly rather than arriving as a flat line.
    const flightTime = dist / speed;
    const lift = kind === 'torpedo' ? 0 : (9.81 * flightTime) / 2;

    this.projectiles.spawn(
      kind,
      from,
      {
        x: (dx / dist) * speed,
        y: (dy / dist) * speed + lift,
        z: (dz / dist) * speed,
      },
      {
        hostile: true,
        damage: spec.weaponDamage,
        ownerId: shooter.id,
        targetId,
      },
    );

    this.events.emit({
      type: 'WeaponFired',
      weapon: 'hostile',
      pos: from,
      dir: { x: dx / dist, y: dy / dist, z: dz / dist },
      hostile: true,
      sourceKind: shooter.kind,
      sourceHeading: shooter.heading,
    });
  }

  // --- Projectiles and damage -----------------------------------------------------------

  /**
   * The 30 mm putting a hostile torpedo down before it arrives.
   *
   * Run before the hit pass, so a torpedo killed this tick cannot also detonate on a hull
   * in the same tick. Both sides are already in the pool and already integrated for this
   * frame; this only asks whether any live player shell is inside `radius` of any live
   * hostile torpedo, and if so releases them both.
   *
   * Deliberately not a shell-versus-everything rule. A shell that clips an incoming
   * rocket is a coincidence the player cannot aim for, and one that clips another shell
   * is noise; the torpedo is the only hostile round slow enough and visible enough to be
   * a target, which is what makes this a skill rather than a dice roll.
   */
  private stepTorpedoIntercepts(dt: number): void {
    const items = this.projectiles.items;
    for (const t of items) {
      if (!t.active || !t.hostile || t.kind !== 'torpedo') continue;
      for (const shell of items) {
        if (!shell.active || shell.hostile || shell.kind !== TORPEDO_INTERCEPT.weapon) continue;

        /*
         * Closest approach over the tick, not distance at the tick boundary.
         *
         * A 30 mm round is still doing several hundred metres a second when it arrives, so
         * it covers about ten metres per step — more than the whole hit volume. Sampling
         * only the endpoints let shells jump clean over a torpedo and out the far side:
         * measured, two hundred rounds held on a torpedo track killed nothing at all. Both
         * bodies are treated as moving at constant velocity across the step, which over
         * 1/60 s is exact enough that drag and gravity do not show.
         */
        const px = shell.pos.x - t.pos.x;
        const py = shell.pos.y - t.pos.y;
        const pz = shell.pos.z - t.pos.z;
        const vx = shell.vel.x - t.vel.x;
        const vy = shell.vel.y - t.vel.y;
        const vz = shell.vel.z - t.vel.z;
        const vv = vx * vx + vy * vy + vz * vz;
        // Time of closest approach, clamped into the step we are actually taking.
        const tc = vv < 1e-9 ? 0 : Math.min(dt, Math.max(0, -(px * vx + py * vy + pz * vz) / vv));
        const miss = Math.hypot(px + vx * tc, py + vy * tc, pz + vz * tc);
        if (miss > TORPEDO_INTERCEPT.radius) continue;

        this.projectiles.release(t);
        this.projectiles.release(shell);
        this.shift.roundsHit++;
        /*
         * The warhead goes off where it was hit, which is the whole point: the player sees
         * the thing they were shooting at die out on the water rather than simply
         * vanishing from the display.
         */
        this.events.emit({
          type: 'Detonation',
          pos: { ...t.pos },
          radius: 30,
          underwater: true,
          depth: Math.max(0, -t.pos.y),
        });
        break;
      }
    }
  }

  private stepProjectilesAndDamage(dt: number): void {
    this.stepTorpedoIntercepts(dt);
    const hits = stepProjectiles(
      this.projectiles, this.vessels, this.waveField, this.time, dt, this.rng,
      this.torpedoSpeedScale,
    );

    for (const hit of hits) {
      const p = hit.projectile;

      if (hit.vessel) {
        this.resolveHullHit(hit.vessel, p, hit.point, hit.ricochet, hit.eye === true);
        // A torpedo warhead does not stop at the hull it touched: the burst is spherical
        // and anything alongside gets its share too.
        if (p.kind === 'torpedo') this.resolveUnderwaterBurst(hit.point, p, hit.vessel);
        continue;
      }

      // Water. A hostile round landing near the rig still counts as a rig hit — that is
      // how incoming fire threatens the platform without needing a rig collision mesh.
      if (p.hostile) {
        const distToRig = Math.hypot(hit.point.x, hit.point.z);
        if (distToRig < 55) {
          const rigDamage = p.damage * RIG_SPLASH_DAMAGE_FACTOR;
          const casualties = applyRigDamage(
            this.resources, rigDamage, this.upgrades.has('blastPlating'), this.rng.next(),
          );
          this.rig.integrity = this.resources.integrity;
          this.rig.alarmTimer = 6;
          // Reported beside the player rather than at the splash a hundred metres below
          // him — see RIG_HIT_REPORT_POINT for why the round itself is left alone.
          this.events.emit({
            type: 'RigHit',
            pos: {
              x: RIG_HIT_REPORT_POINT.x + (this.rng.next() - 0.5) * RIG_HIT_REPORT_SPREAD,
              y: RIG_HIT_REPORT_POINT.y + (this.rng.next() - 0.5) * RIG_HIT_REPORT_SPREAD,
              z: RIG_HIT_REPORT_POINT.z + (this.rng.next() - 0.5) * RIG_HIT_REPORT_SPREAD,
            },
            damage: rigDamage,
            casualties,
            cause: 'gunfire',
          });
        }
      }

      /*
       * A hit whose projectile is still active is a pass-through — the depth charge
       * crossing the surface on its way down, or a torpedo starting its run — while a
       * released one is terminal. The distinction matters here because a depth charge
       * now reports both, and only the first is a splash: at fuse depth the eruption
       * is the event, and a splash column thirty metres under it is neither audible
       * nor visible.
       */
      const terminal = !p.active;
      if (!(p.kind === 'depthCharge' && terminal)) {
        this.events.emit({
          type: 'ImpactWater',
          pos: { ...hit.point },
          kind: p.kind,
          speed: hit.speed,
          hostile: p.hostile,
          ricochet: hit.ricochet,
        });
      }

      if (p.kind === 'depthCharge' && !terminal) continue;

      if (p.kind === 'depthCharge' && this.kraken) {
        const boss = this.vessels.find((v) => v.kind === 'kraken' && v.state !== 'dead');
        if (boss) {
          /*
           * Measured in three dimensions, not two.
           *
           * This was horizontal distance alone, which meant the fuse setting had no
           * bearing on whether a charge counted toward forcing the creature up — a charge
           * detonating at 10 m over something sitting at 45 counted exactly as much as one
           * that reached it. With `submergedDepth` real, the depth is now part of the
           * measurement and the deepest setting is what this phase answers to.
           */
          registerChargeNear(
            this.kraken,
            Math.hypot(
              boss.pos.x - hit.point.x,
              (boss.pos.y - boss.submergedDepth) - hit.point.y,
              boss.pos.z - hit.point.z,
            ),
          );
        }
      }

      if (p.kind === 'depthCharge') {
        /*
         * The charge reached its fuse depth. Damage is computed in 3D, so a shallow fuse
         * chips surface craft while a deep one is the sub-killer — that difference is the
         * entire skill of the weapon and the reason the selector exists.
         */
        this.resolveUnderwaterBurst(hit.point, p, null);
        this.events.emit({
          type: 'Detonation',
          pos: { ...hit.point },
          radius: DEPTH_CHARGE.falloffRadius,
          underwater: true,
          depth: Math.max(0, -hit.point.y),
        });
      } else if (p.kind === 'torpedo') {
        // A torpedo that ran out of fuel simply sinks; only a fused one detonates, and
        // that path goes through the hull branch above.
        this.events.emit({
          type: 'Detonation', pos: { ...hit.point }, radius: 40, underwater: false, depth: 0,
        });
      }
    }
  }

  /**
   * Spherical damage from an underwater burst.
   *
   * `alreadyHit` is the hull the projectile physically struck, which has taken its full
   * contact damage through the normal path and must not be double-counted here.
   */
  private resolveUnderwaterBurst(centre: Vec3, p: Projectile, alreadyHit: Vessel | null): void {
    const lethal = p.kind === 'depthCharge' ? DEPTH_CHARGE.lethalRadius : 12;
    const falloff = p.kind === 'depthCharge' ? DEPTH_CHARGE.falloffRadius : 30;
    const base = p.kind === 'depthCharge' ? DEPTH_CHARGE.damage : p.damage * 0.35;

    for (const { vessel, amount } of detonationDamage(
      centre, this.vessels, lethal, falloff, base,
    )) {
      if (vessel === alreadyHit) continue;
      if (vessel.safe) continue;
      /*
       * The Kraken is felt but never killed here either.
       *
       * A direct hit on the creature already runs through its own phase-aware armour and
       * is floored at 1 hp, because the phase machine — not the health pool — decides
       * when it dies; a boss deleted mid-fight leaves the encounter with nothing left to
       * advance it. Splash damage bypassed all of that: it used the ordinary armour
       * matrix and subtracted freely.
       *
       * That hole was harmless only because depth charges never reached the creature —
       * the charge struck its hull on the way down and stopped there instead of sinking
       * to its fuse depth. Once a submerged Kraken stopped blocking projectiles, the
       * charges detonated where they were aimed, the splash finally landed, and the
       * creature died underwater at -45 hp: no second surfacing, and the authored death
       * never played. Measured, not supposed.
       */
      if (vessel.kind === 'kraken' && this.kraken) {
        // A charge that erupts against a surfaced mantle counts toward the phase too;
        // it is the answer for a player who arrived without enough torpedoes.
        if (!p.hostile) registerMantleHit(this.kraken, p.kind);
        const factor = krakenDamageFactor(this.kraken, p.kind);
        if (factor <= 0) continue;
        vessel.hp = Math.max(1, vessel.hp - amount * factor);
        this.events.emit({
          type: 'ImpactHull',
          pos: { ...centre },
          kind: p.kind,
          targetId: vessel.id,
          damage: amount * factor,
          ricochet: false,
        });
        continue;
      }
      const table = ARMOUR_MATRIX[p.kind] ?? ARMOUR_MATRIX['shell'];
      const spec = vesselSpec(vessel.kind);
      const scaled = amount * (table?.[spec.armour] ?? 1);
      vessel.hp -= scaled;
      this.events.emit({
        type: 'ImpactHull',
        pos: { ...centre },
        kind: p.kind,
        targetId: vessel.id,
        damage: scaled,
        ricochet: false,
      });
      this.killIfDead(vessel);
    }
  }

  private resolveHullHit(
    target: Vessel, p: Projectile, point: Vec3, ricochet: boolean, eye = false,
  ): void {
    // Past the Safe Line she is out of the fight — nothing reaches her, including a
    // stray round of the player's own. See `SAFE_LINE_METRES`.
    if (target.safe) return;

    const spec = vesselSpec(target.kind);

    /*
     * The Kraken has its own armour model, because "armoured" is not expressive enough
     * for a boss whose whole design is that different weapons matter in different
     * phases. A shell into the mantle does nothing at all — deliberately zero rather
     * than a token amount, so the player learns in one burst instead of wasting a
     * magazine finding out.
     */
    if (target.kind === 'kraken' && this.kraken) {
      /*
       * The coup de grâce. A round into the staring eye is counted by the phase machine,
       * not the health pool — eight of them and the creature dies on camera. The impact
       * event is solid rather than a ricochet: rounds into the eye must visibly LAND.
       */
      if (eye && p.kind === 'shell' && !p.hostile) {
        registerEyeHit(this.kraken);
        this.events.emit({
          type: 'ImpactHull',
          pos: { ...point },
          kind: p.kind,
          targetId: target.id,
          damage: AUTOCANNON.damage * KRAKEN.eyeDamageMultiplier,
          ricochet: false,
        });
        return;
      }
      const factor = krakenDamageFactor(this.kraken, p.kind);
      /*
       * Every player weapon moves the phase, in torpedo-equivalents. Hostile fire does
       * not: a mini-sub's fish crossing the mantle must not fight the creature for you.
       */
      if (!p.hostile) registerMantleHit(this.kraken, p.kind);
      if (factor <= 0) {
        this.events.emit({
          type: 'ImpactHull',
          pos: { ...point },
          kind: p.kind,
          targetId: target.id,
          damage: 0,
          ricochet: true,
        });
        return;
      }
      /*
       * Damage is felt but never fatal. The phase machine decides when the Kraken dies,
       * and the HP pool exists only so the health readout means something — otherwise a
       * lucky eye window (nine times damage, thirty rounds inside six seconds) simply
       * deletes the boss mid-fight, the vessel disappears, and the encounter stalls with
       * no creature left to advance it. That is exactly what happened.
       */
      target.hp = Math.max(1, target.hp - p.damage * factor);
      this.events.emit({
        type: 'ImpactHull',
        pos: { ...point },
        kind: p.kind,
        targetId: target.id,
        damage: p.damage * factor,
        ricochet: false,
      });
      return;
    }

    // Armour matrix, with the ammunition upgrades selecting a different row.
    let table = ARMOUR_MATRIX[p.kind] ?? ARMOUR_MATRIX['shell'];
    if (p.kind === 'shell' && !p.hostile) {
      if (this.upgrades.has('apAmmo')) table = ARMOUR_MATRIX['shellAP'];
      else if (this.upgrades.has('heAmmo')) table = ARMOUR_MATRIX['shellHE'];
    }
    const factor = table?.[spec.armour] ?? 1;
    const damage = p.damage * factor;
    /*
     * Every fifth autocannon round is a visible tracer. On an armoured hull that tracer
     * glances away instead of disappearing into the same spark puff as every other hit.
     *
     * This is deliberately keyed to the authored tracer cadence, not another random
     * draw: it gives the plan's one-in-five visual ricochet, guarantees the deflection
     * itself is readable, and cannot perturb any later gameplay roll. Damage still uses
     * the armour matrix unchanged — `ricochet` is presentation, not a second armour rule.
     */
    const armourRicochet = p.kind === 'shell' && spec.armour === 'armoured' && p.tracer;

    target.hp -= damage;
    if (!p.hostile) this.shift.roundsHit++;

    this.events.emit({
      type: 'ImpactHull',
      pos: { ...point },
      kind: p.kind,
      targetId: target.id,
      damage,
      ricochet: ricochet || armourRicochet,
    });

    this.killIfDead(target);
  }

  /**
   * Transition a vessel to sinking and settle its bookkeeping.
   *
   * Deliberately keyed off HP rather than called from the damage site: area effects,
   * scripted losses and future creature attacks all reduce HP by different paths, and a
   * hull at zero must sink regardless of which one emptied it.
   */
  private killIfDead(target: Vessel): void {
    if (target.hp > 0) return;
    if (target.state === 'sinking' || target.state === 'dead') return;

    const spec = vesselSpec(target.kind);
    target.state = 'sinking';
    target.stateTimer = 0;
    target.hp = 0;

    if (this.rng.next() < spec.criticalChance) {
      this.events.emit({
        type: 'Detonation', pos: { ...target.pos }, radius: 30, underwater: false, depth: 0,
      });
    }

    this.events.emit({
      type: 'ShipLost',
      id: target.id,
      kind: target.kind,
      faction: target.faction,
      pos: { ...target.pos },
    });

    if (target.faction === 'hostile') {
      this.shift.hostilesDestroyed++;
    } else if (!target.resolved) {
      target.resolved = true;
      this.shift.arrivalsLost++;
      const summary = applyLoss(this.resources, target.cargo);
      /*
       * The spoken half is fixed and the written half is not. This line interpolates a
       * cargo summary, which no bake could ever cover — so the voice says the sentence
       * that is always true and the HUD carries what was actually on board.
       */
      const spoken = target.kind === 'tanker' ? SYSTEM_LINES.tankerLost : SYSTEM_LINES.supplyLost;
      this.events.emit({
        type: 'Radio',
        speaker: 'RIG CONTROL',
        text: `Vessel lost — ${summary}.`,
        read: spoken.read,
      });
    }
  }

  // --- Economy and end conditions -------------------------------------------------------

  private stepEconomy(dt: number): void {
    const bonus = this.upgrades.has('productionBoost') ? 150 : 0;
    if (this.upgrades.has('storageTanks')) this.resources.oilCapacity = ECONOMY.storageCap + 6000;
    tickProduction(this.resources, dt, bonus);

    if (this.upgrades.has('repairDrones')) {
      this.resources.integrity = clamp(this.resources.integrity + (2 / 60) * dt, 0, 100);
    }
    this.rig.integrity = this.resources.integrity;
    this.rig.alarmTimer = Math.max(0, this.rig.alarmTimer - dt);
  }

  private checkEndConditions(dt: number): void {
    if (isDefeated(this.resources)) {
      this.shift.outcome = 'defeat';
      this.events.emit({ type: 'ShiftEnd', outcome: 'defeat' });
      return;
    }

    /*
     * The outro, once the shift has been won: see `SHIFT_OUTRO_SECONDS`.
     *
     * Nothing can un-win a shift from here — every beat has fired, so no hostile can
     * arrive — which is why this is a plain countdown rather than a condition that keeps
     * being re-tested.
     */
    if (this.outroTimer > 0) {
      this.outroTimer -= dt;
      if (this.outroTimer <= 0) {
        this.shift.outcome = 'complete';
        this.events.emit({ type: 'ShiftEnd', outcome: 'complete' });
      }
      return;
    }

    // The shift is over when every scheduled arrival has resolved and no hostile is
    // still in the fight. Sinking wrecks and outbound ships are presentation and must
    // never hold a shift open.
    const allBeatsFired = this.beats.every((b) => b.fired);
    /*
     * A shift with no scheduled arrivals is finished when the fighting is.
     *
     * The original condition also required at least one arrival, which is right for
     * eleven shifts and wrong for the twelfth: the Kraken shift escorts nobody, so it
     * could only ever end on the watchdog or on defeat. A boss you beat and then wait
     * out is not a victory.
     */
    const arrivalsDone = this.shift.arrivalsScheduled === 0
      || this.shift.arrivalsSafe + this.shift.arrivalsLost >= this.shift.arrivalsScheduled;
    // The finale is not over until the creature is actually gone.
    const bossResolved = !this.kraken || this.kraken.phase === 'gone';
    // A hostile on its way out of the area is no longer part of the fight.
    const hostilesLeft = this.vessels.some(
      (v) => v.faction === 'hostile' && v.state !== 'dead'
        && v.state !== 'sinking' && v.state !== 'departing',
    );

    if (allBeatsFired && arrivalsDone && bossResolved && !hostilesLeft) {
      this.outroTimer = SHIFT_OUTRO_SECONDS;
      // A watch that lost a hull does not get the relieved sign-off. See SYSTEM_LINES.
      const signOff = this.shift.arrivalsLost > 0
        ? SYSTEM_LINES.standDownLost
        : SYSTEM_LINES.standDown;
      this.events.emit({
        type: 'Radio',
        speaker: 'RIG CONTROL',
        text: signOff.text,
        read: signOff.read,
        final: true,
      });
      return;
    }

    // Hard watchdog. Surviving hostiles withdraw and pending outcomes are computed.
    if (this.shift.elapsed >= this.script.maxDuration) {
      for (const v of this.vessels) {
        if (v.faction === 'hostile' && v.state !== 'dead') v.state = 'dead';
      }
      this.shift.outcome = 'complete';
      this.events.emit({ type: 'ShiftEnd', outcome: 'complete' });
    }
  }

  // --- Queries used by the renderer and UI ------------------------------------------------

  snapshot(): SimSnapshot {
    return {
      time: this.time,
      vessels: this.vessels,
      projectiles: this.projectiles.items,
      resources: this.resources,
      rig: this.rig,
      shift: this.shift,
      selectedId: this.selectedId,
      torpedoLock: {
        targetId: this.torpedoLockId,
        progress: this.torpedoLockFraction,
        locked: this.torpedoLocked,
      },
      gunLock: {
        targetId: this.gunLockId,
        progress: this.gunLockFraction,
        locked: this.gunLocked,
      },
      dispersionCone: this.dispersionCone(),
      projectorMaxRange: projectorEnvelope(this.upgrades.has('projectorRails')).maxRange,
    };
  }

  /** Water height under a world point — the renderer uses this for splash placement. */
  surfaceAt(x: number, z: number): number {
    return waterHeightAt(this.waveField, x, z, this.time);
  }

  /**
   * Firing solution for the selected target: where to aim so a shell arrives where the
   * target will be. This is what the scope's lead pip draws — the shell still flies
   * ballistically, so the pip is an aid, never an auto-hit.
   */
  leadSolution(target: Vessel): { point: Vec3; flightTime: number } | null {
    const dx = target.pos.x - GUN_MOUNT.x;
    const dz = target.pos.z - GUN_MOUNT.z;
    const range = Math.hypot(dx, dz);
    if (range < 1) return null;

    // Two passes. The first gets an approximate flight time from the target's present
    // position; the second re-solves for where it will actually be, since a fast boat
    // moves a long way in the seconds a shell is airborne.
    let flight = timeOfFlight(range, solveElevation(range, target.pos.y - GUN_MOUNT.y));
    if (!Number.isFinite(flight)) return null;

    const dir = dirFromHeading(target.heading);
    let leadX = target.pos.x + dir.x * target.speed * flight;
    let leadZ = target.pos.z + dir.z * target.speed * flight;

    const leadRange = Math.hypot(leadX - GUN_MOUNT.x, leadZ - GUN_MOUNT.z);
    const leadDrop = target.pos.y + 4 - GUN_MOUNT.y;
    const elevation = solveElevation(leadRange, leadDrop);
    flight = timeOfFlight(leadRange, elevation);
    if (!Number.isFinite(flight)) return null;

    leadX = target.pos.x + dir.x * target.speed * flight;
    leadZ = target.pos.z + dir.z * target.speed * flight;

    /*
     * Return the point to *aim at*, not the point the target will occupy.
     *
     * These are not the same, and conflating them is why the gun felt impossible. A shell
     * fired straight at the predicted position falls below it on the way — five metres at
     * 1 km, twenty-one at 2 km, ninety-four at 4 km. The pip sat on the target's future
     * position, so putting the crosshair on the pip aimed low by exactly the drop and
     * every round fell short. The pip now sits on the barrel line that actually connects:
     * bearing to the lead point, raised by the ballistic elevation.
     */
    const bearing = Math.atan2(leadX - GUN_MOUNT.x, leadZ - GUN_MOUNT.z);
    const horizontal = Math.hypot(leadX - GUN_MOUNT.x, leadZ - GUN_MOUNT.z);
    const aim: Vec3 = {
      x: GUN_MOUNT.x + Math.sin(bearing) * horizontal,
      y: GUN_MOUNT.y + Math.tan(elevation) * horizontal,
      z: GUN_MOUNT.z + Math.cos(bearing) * horizontal,
    };
    return { point: aim, flightTime: flight };
  }

  /** Elevation angle the gun needs for a target at this horizontal range. */
  elevationFor(range: number, heightDrop: number): number {
    return solveElevation(range, heightDrop);
  }
}

/** Rotate a unit direction by small yaw/pitch offsets — used for dispersion. */
function perturb(dir: Vec3, yaw: number, pitch: number): Vec3 {
  // Build an orthonormal basis around `dir` and offset within it. For the tiny angles
  // dispersion uses, the small-angle form is exact enough and avoids trig per round.
  const up = Math.abs(dir.y) > 0.99 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  const rx = dir.y * up.z - dir.z * up.y;
  const ry = dir.z * up.x - dir.x * up.z;
  const rz = dir.x * up.y - dir.y * up.x;
  const rl = Math.hypot(rx, ry, rz) || 1;
  const ux = rx / rl, uy = ry / rl, uz = rz / rl;
  const vx = uy * dir.z - uz * dir.y;
  const vy = uz * dir.x - ux * dir.z;
  const vz = ux * dir.y - uy * dir.x;

  const x = dir.x + ux * yaw + vx * pitch;
  const y = dir.y + uy * yaw + vy * pitch;
  const z = dir.z + uz * yaw + vz * pitch;
  const l = Math.hypot(x, y, z) || 1;
  return { x: x / l, y: y / l, z: z / l };
}

/**
 * Fold ordered manifest items into a vessel's cargo.
 *
 * Additive on top of whatever the script already gave her, so a scripted delivery and a
 * player order can ride on the same hull without either being silently dropped.
 */
function mergeManifest(base: Cargo | undefined, itemIds: readonly string[]): Cargo {
  const cargo: Cargo = base ? { ...base } : { label: '' };
  const labels: string[] = base?.label ? [base.label] : [];

  for (const id of itemIds) {
    const item = MANIFEST_ITEMS.find((m) => m.id === id);
    if (!item) continue;
    if (item.ammo) cargo.ammo = (cargo.ammo ?? 0) + item.ammo;
    if (item.torpedoes) cargo.torpedoes = (cargo.torpedoes ?? 0) + item.torpedoes;
    if (item.depthCharges) cargo.depthCharges = (cargo.depthCharges ?? 0) + item.depthCharges;
    if (item.repairPercent) cargo.repairPercent = (cargo.repairPercent ?? 0) + item.repairPercent;
    if (item.crew) cargo.crew = (cargo.crew ?? 0) + item.crew;
    labels.push(item.label);
  }

  cargo.label = labels.join(' · ') || 'ordered manifest';
  return cargo;
}

/**
 * Piecewise-linear read of TORPEDO_LOCK.hitCurve: zero below the first point, the last
 * point's value beyond it. The curve's exact numbers are Adam's and binding — exported
 * so the tests can hold them to the letter.
 */
export function torpedoHitChance(range: number): number {
  const curve = TORPEDO_LOCK.hitCurve;
  const first = curve[0] as readonly [number, number];
  if (range < first[0]) return 0;
  for (let i = 1; i < curve.length; i++) {
    const [r1, p1] = curve[i] as readonly [number, number];
    const [r0, p0] = curve[i - 1] as readonly [number, number];
    if (range <= r1) return p0 + ((range - r0) / (r1 - r0)) * (p1 - p0);
  }
  return (curve[curve.length - 1] as readonly [number, number])[1];
}

/** Cargo manifests referenced by shift scripts. */
function cargoFor(kind: NonNullable<SpawnBeat['cargo']>): Cargo {
  switch (kind) {
    case 'oil': return { oilCollection: true, label: 'Crude collection' };
    case 'ammo': return { ammo: 900, label: '900 rounds' };
    case 'repair': return { repairPercent: 20, label: 'Repair kit' };
    case 'crew': return { crew: 12, label: 'Crew team' };
    case 'torpedo': return { torpedoes: 2, label: '2 torpedoes' };
    case 'mixed': return { ammo: 900, repairPercent: 12, crew: 6, label: 'Mixed manifest' };
    /*
     * The set shift 4 has always said Kestrel was carrying. Stores ride along with it so
     * the run is still worth defending on its own terms if the player already bought one.
     */
    case 'hydrophone':
      return { upgrade: 'hydrophone', ammo: 600, repairPercent: 8, label: 'Hydrophone array' };
  }
}
